/**
 * Who a canvas input event belongs to: a front-end layered over the game (the windowing UI, a HUD), or the
 * game itself.
 *
 * Front-ends are asked first, in order, whether they claim an event; whatever none of them claims goes to
 * the game's handlers. A press is owned whole: if a front-end claims the `pointerdown`, every move and the
 * release of that pointer are its too, so a drag that starts on a window and leaves it never leaks a
 * half-gesture into the game.
 *
 * The router does not deliver to front-ends. A front-end listens on the canvas itself, as the windowing UI
 * does; what it tells the router is only whether the game should hear about the event as well.
 */

export type PointerKind = "pointerdown" | "pointermove" | "pointerup" | "pointercancel";
export type KeyKind = "keydown" | "keyup";

export interface InputClaimant {
  /** True when this front-end takes a pointer press, or a wheel, at that canvas position (CSS px). */
  claimsPointer(x: number, y: number, e: PointerEvent | WheelEvent): boolean;
  /** True when this front-end takes keyboard input right now, e.g. because one of its controls has focus. */
  claimsKeys(e: KeyboardEvent): boolean;
}

export interface GameInputHandlers {
  pointerdown?: (e: PointerEvent) => void;
  pointermove?: (e: PointerEvent) => void;
  pointerup?: (e: PointerEvent) => void;
  pointercancel?: (e: PointerEvent) => void;
  wheel?: (e: WheelEvent) => void;
  keydown?: (e: KeyboardEvent) => void;
  keyup?: (e: KeyboardEvent) => void;
}

type Listener<E> = (e: E) => void;

export class InputRouter {
  readonly canvas: HTMLCanvasElement;
  #claimants: InputClaimant[] = [];
  #listeners = new Map<string, Set<Listener<never>>>();
  /** Pointers whose press a front-end claimed, until they lift. */
  #owned = new Set<number>();
  /** Keys held while the game had the keyboard, so their release reaches it whoever has focus by then. */
  #gameKeys = new Set<string>();
  #down = new Set<string>();
  #pointer = { x: 0, y: 0 };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    // A canvas takes key events only when it can take focus.
    if (canvas.tabIndex < 0 && !canvas.hasAttribute("tabindex")) canvas.tabIndex = 0;
    if (!canvas.style.touchAction) canvas.style.touchAction = "none";
    canvas.addEventListener("pointerdown", this.#onPointer);
    canvas.addEventListener("pointermove", this.#onPointer);
    canvas.addEventListener("pointerup", this.#onPointer);
    canvas.addEventListener("pointercancel", this.#onPointer);
    canvas.addEventListener("wheel", this.#onWheel, { passive: true });
    canvas.addEventListener("keydown", this.#onKey);
    canvas.addEventListener("keyup", this.#onKey);
    window.addEventListener("blur", this.#onBlur);
  }

  /** Add a front-end. Earlier claimants are asked first. Returns a function that removes it. */
  addClaimant(claimant: InputClaimant): () => void {
    this.#claimants.push(claimant);
    return () => {
      const i = this.#claimants.indexOf(claimant);
      if (i >= 0) this.#claimants.splice(i, 1);
    };
  }

  /** Listen for input the front-ends did not claim. Returns a function that stops listening. */
  on<K extends keyof GameInputHandlers>(type: K, handler: NonNullable<GameInputHandlers[K]>): () => void {
    let set = this.#listeners.get(type);
    if (!set) this.#listeners.set(type, (set = new Set()));
    set.add(handler as Listener<never>);
    return () => set.delete(handler as Listener<never>);
  }

  /** Whether a key (`KeyboardEvent.code`) is held, as far as the game has been told. */
  isDown(code: string): boolean {
    return this.#down.has(code);
  }

  /** The last pointer position the game was told about, in CSS px relative to the canvas. */
  get pointer(): { readonly x: number; readonly y: number } {
    return this.#pointer;
  }

  #emit(type: string, e: Event): void {
    const set = this.#listeners.get(type);
    if (!set) return;
    for (const handler of [...set]) (handler as Listener<Event>)(e);
  }

  #local(e: { clientX: number; clientY: number }): { x: number; y: number } {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  #onPointer = (e: PointerEvent): void => {
    const p = this.#local(e);
    let owned = this.#owned.has(e.pointerId);
    if (e.type === "pointerdown") {
      owned = this.#claimants.some((c) => c.claimsPointer(p.x, p.y, e));
      if (owned) this.#owned.add(e.pointerId);
    } else if (e.type === "pointermove" && !owned && e.buttons === 0) {
      // A hover over a window is the window's; the game hears only about the pointer over open ground.
      owned = this.#claimants.some((c) => c.claimsPointer(p.x, p.y, e));
    }
    if (e.type === "pointerup" || e.type === "pointercancel") this.#owned.delete(e.pointerId);
    if (owned) return;
    this.#pointer = p;
    this.#emit(e.type, e);
  };

  #onWheel = (e: WheelEvent): void => {
    const p = this.#local(e);
    if (this.#claimants.some((c) => c.claimsPointer(p.x, p.y, e))) return;
    this.#emit("wheel", e);
  };

  #onKey = (e: KeyboardEvent): void => {
    if (e.type === "keyup") {
      // The release goes wherever the press went.
      if (!this.#gameKeys.delete(e.code)) return;
      this.#down.delete(e.code);
      this.#emit("keyup", e);
      return;
    }
    if (this.#claimants.some((c) => c.claimsKeys(e))) return;
    this.#gameKeys.add(e.code);
    this.#down.add(e.code);
    this.#emit("keydown", e);
  };

  /** A window that loses focus never sees its keys come up; let go of all of them. */
  #onBlur = (): void => {
    for (const code of this.#gameKeys) {
      this.#emit("keyup", new KeyboardEvent("keyup", { code }));
    }
    this.#gameKeys.clear();
    this.#down.clear();
  };

  dispose(): void {
    const c = this.canvas;
    c.removeEventListener("pointerdown", this.#onPointer);
    c.removeEventListener("pointermove", this.#onPointer);
    c.removeEventListener("pointerup", this.#onPointer);
    c.removeEventListener("pointercancel", this.#onPointer);
    c.removeEventListener("wheel", this.#onWheel);
    c.removeEventListener("keydown", this.#onKey);
    c.removeEventListener("keyup", this.#onKey);
    window.removeEventListener("blur", this.#onBlur);
    this.#listeners.clear();
    this.#claimants.length = 0;
  }
}
